/**
 * Product card enhancements — collection page
 * - Prev/next navigation for the media gallery on each product card, with a real
 *   sliding-track transition (not just a crossfade) so the swipe motion is visible
 * - Touch/mouse drag follows the finger/cursor in real time, then snaps to the
 *   nearest slide on release — matching a native carousel feel
 * - Hover-to-preview: hovering the card alone shows the second image, no click needed
 * - Hover-to-reveal size bar that adds the selected size straight to cart
 *
 * Integrates with the theme's existing <form is="product-form"> element so that
 * the standard cart-drawer / cart:change events keep working exactly as they do
 * for the normal Add to cart button.
 */
(function () {
  function getTrack(card) {
    return card.querySelector('[data-card-media-track]');
  }

  function getItems(track) {
    return Array.prototype.slice.call(track.querySelectorAll('[data-card-media-item]'));
  }

  function getCurrentIndex(track) {
    var stored = parseInt(track.dataset.currentIndex, 10);
    return isNaN(stored) ? 0 : stored;
  }

  function setTrackPosition(track, index, animate) {
    var itemCount = getItems(track).length;
    if (!itemCount) return;

    var safeIndex = ((index % itemCount) + itemCount) % itemCount;
    track.dataset.currentIndex = safeIndex;

    if (!animate) {
      track.classList.add('is-dragging'); // reuse to temporarily disable transition
    }

    track.style.transform = 'translateX(-' + (safeIndex * 100) + '%)';

    if (!animate) {
      // Force reflow so the no-transition jump applies before we re-enable it
      track.offsetHeight;
      track.classList.remove('is-dragging');
    }
  }

  function showImageAt(card, targetIndex, animate) {
    var track = getTrack(card);
    if (!track) return;
    if (getItems(track).length < 2) return;
    setTrackPosition(track, targetIndex, animate !== false);
  }

  function cycleMedia(card, direction) {
    var track = getTrack(card);
    if (!track) return;
    if (getItems(track).length < 2) return;

    var currentIndex = getCurrentIndex(track);
    var nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    setTrackPosition(track, nextIndex, true);
  }

  function handleSizeClick(button) {
    if (button.disabled || button.classList.contains('is-loading')) return;

    var card = button.closest('product-card');

    if (!card) {
      var sheet = button.closest('[data-card-size-sheet]');
      card = sheet ? sheet.__ownerCard : null;
    }

    if (!card) return;

    var form = card.querySelector('.product-card__quick-add-form');
    if (!form) return;

    var input = form.querySelector('input[name="id"]');
    if (!input) return;

    input.value = button.getAttribute('data-variant-id');

    button.classList.add('is-loading');

    var onAdd = function () {
      form.removeEventListener('variant:add', onAdd);
      form.removeEventListener('cart:error', onError);
      button.classList.remove('is-loading');
      button.classList.add('is-added');

      window.setTimeout(function () {
        button.classList.remove('is-added');
        closeSizeSheet(card);
      }, 700);
    };

    var onError = function () {
      form.removeEventListener('variant:add', onAdd);
      form.removeEventListener('cart:error', onError);
      button.classList.remove('is-loading');
    };

    form.addEventListener('variant:add', onAdd);
    form.addEventListener('cart:error', onError);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }

  function bindHoverPreview(card) {
    if (card.dataset.hoverPreviewBound === 'true') return;
    card.dataset.hoverPreviewBound = 'true';

    card.addEventListener('mouseenter', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      showImageAt(card, 1, true);
    });

    card.addEventListener('mouseleave', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      showImageAt(card, 0, true);
    });
  }

  function bindSwipe(card) {
    if (card.dataset.swipeBound === 'true') return;
    card.dataset.swipeBound = 'true';

    var figure = card.querySelector('.product-card__figure');
    var track = getTrack(card);
    if (!figure || !track) return;

    var SWIPE_THRESHOLD_RATIO = 0.18; // fraction of the card's width needed to commit to a slide change
    var DIRECTION_LOCK_THRESHOLD = 6; // px of movement before we decide horizontal vs vertical

    var startX = 0;
    var startY = 0;
    var tracking = false;
    var draggingHorizontally = false;
    var directionDecided = false;
    var startIndex = 0;
    var containerWidth = 0;

    // rAF batching: only the latest pointer position is applied, once per frame,
    // instead of writing to the DOM on every single touchmove/mousemove event.
    var rafId = null;
    var pendingX = null;
    var pendingY = null;

    function scheduleUpdate(clientX, clientY) {
      pendingX = clientX;
      pendingY = clientY;

      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(function () {
        rafId = null;
        if (pendingX !== null) applyDrag(pendingX, pendingY);
      });
    }

    function cancelScheduledUpdate() {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingX = null;
      pendingY = null;
    }

    function beginDrag(clientX, clientY) {
      var itemCount = getItems(track).length;
      if (itemCount < 2) return false;

      containerWidth = figure.getBoundingClientRect().width || 1;
      startX = clientX;
      startY = clientY;
      startIndex = getCurrentIndex(track);
      draggingHorizontally = false;
      directionDecided = false;
      track.classList.add('is-dragging');
      return true;
    }

    function applyDrag(clientX, clientY) {
      var deltaX = clientX - startX;
      var deltaY = clientY - startY;

      if (!directionDecided) {
        var distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (distance < DIRECTION_LOCK_THRESHOLD) return;

        // Require a clear (not marginal) horizontal bias before committing, so a
        // slightly-off swipe still registers instead of randomly falling back
        // to "let the page scroll" on some attempts but not others.
        directionDecided = true;
        draggingHorizontally = Math.abs(deltaX) > Math.abs(deltaY) * 1.15;
      }

      if (!draggingHorizontally) return;

      var itemCount = getItems(track).length;
      var basePercent = -(startIndex * 100);
      var dragPercent = (deltaX / containerWidth) * 100;

      // Rubber-band slightly at the very first/last slide so it's clear there's nothing further.
      var projectedIndex = startIndex - (deltaX / containerWidth);
      if (projectedIndex < 0 || projectedIndex > itemCount - 1) {
        dragPercent *= 0.35;
      }

      track.style.transform = 'translateX(' + (basePercent + dragPercent) + '%)';
    }

    function endDrag(clientX) {
      cancelScheduledUpdate();
      track.classList.remove('is-dragging');

      var deltaX = clientX - startX;
      var ratio = deltaX / containerWidth;

      if (!draggingHorizontally || Math.abs(ratio) < SWIPE_THRESHOLD_RATIO) {
        setTrackPosition(track, startIndex, true); // snap back
        return;
      }

      setTrackPosition(track, startIndex + (ratio < 0 ? 1 : -1), true);
    }

    // --- Touch ---
    figure.addEventListener('touchstart', function (event) {
      if (event.target.closest('button')) {
        tracking = false;
        return;
      }

      var touch = event.touches[0];
      tracking = beginDrag(touch.clientX, touch.clientY);
    }, { passive: true });

    figure.addEventListener('touchmove', function (event) {
      if (!tracking) return;

      var touch = event.touches[0];

      // Decide direction immediately (cheap check, no DOM write) so preventDefault
      // can be called on the very same event that crosses the lock threshold —
      // waiting a frame here is what causes swipes to intermittently "not catch".
      if (!directionDecided) {
        var deltaX = touch.clientX - startX;
        var deltaY = touch.clientY - startY;
        var distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance >= DIRECTION_LOCK_THRESHOLD) {
          directionDecided = true;
          draggingHorizontally = Math.abs(deltaX) > Math.abs(deltaY) * 1.15;
        }
      }

      if (draggingHorizontally) {
        event.preventDefault();
      }

      scheduleUpdate(touch.clientX, touch.clientY);
    }, { passive: false });

    figure.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;

      var touch = event.changedTouches[0];
      endDrag(touch.clientX);
    });

    figure.addEventListener('touchcancel', function () {
      if (!tracking) return;
      tracking = false;
      cancelScheduledUpdate();
      track.classList.remove('is-dragging');
      setTrackPosition(track, startIndex, true);
    });

    // --- Desktop mouse drag ---
    var mouseTracking = false;

    figure.addEventListener('mousedown', function (event) {
      if (event.target.closest('button')) return;
      if (!window.matchMedia('(hover: hover)').matches) return;

      mouseTracking = beginDrag(event.clientX, event.clientY);
      if (mouseTracking) event.preventDefault();
    });

    document.addEventListener('mousemove', function (event) {
      if (!mouseTracking) return;
      scheduleUpdate(event.clientX, event.clientY);
    });

    document.addEventListener('mouseup', function (event) {
      if (!mouseTracking) return;
      mouseTracking = false;
      endDrag(event.clientX);
    });

    // Prevent the browser's native "drag the image" ghost/behaviour
    figure.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('draggable', 'false');
    });
  }

  function relocateSizeSheet(card) {
    if (card.dataset.sizeSheetRelocated === 'true') return;
    if (!window.matchMedia('(hover: none)').matches) return;

    var sheet = card.querySelector('[data-card-size-sheet]');
    var backdrop = card.querySelector('[data-card-size-backdrop]');

    if (sheet) document.body.appendChild(sheet);
    if (backdrop) document.body.appendChild(backdrop);

    // Keep a reference back to the card so open/close logic can still find
    // the right sheet even though it now lives outside the card in the DOM.
    if (sheet) sheet.__ownerCard = card;
    if (backdrop) backdrop.__ownerCard = card;

    card.dataset.sizeSheetRelocated = 'true';
  }

  function bindAllCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll ? scope.querySelectorAll('product-card') : [];
    Array.prototype.forEach.call(cards, function (card) {
      bindHoverPreview(card);
      bindSwipe(card);
      relocateSizeSheet(card);
    });
  }

  function getSheetForCard(card) {
    // The sheet may have been moved out to <body>, so we can no longer rely
    // on card.querySelector for it — walk document sheets and match owner.
    var sheets = document.querySelectorAll('[data-card-size-sheet]');
    for (var i = 0; i < sheets.length; i++) {
      if (sheets[i].__ownerCard === card) return sheets[i];
    }
    return card ? card.querySelector('[data-card-size-sheet]') : null;
  }

  function getBackdropForCard(card) {
    var backdrops = document.querySelectorAll('[data-card-size-backdrop]');
    for (var i = 0; i < backdrops.length; i++) {
      if (backdrops[i].__ownerCard === card) return backdrops[i];
    }
    return card ? card.querySelector('[data-card-size-backdrop]') : null;
  }

  function openSizeSheet(card) {
    var sheet = getSheetForCard(card);
    var backdrop = getBackdropForCard(card);
    if (sheet) sheet.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
  }

  function closeSizeSheet(card) {
    var sheet = getSheetForCard(card);
    var backdrop = getBackdropForCard(card);
    if (sheet) sheet.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
  }

  document.addEventListener('click', function (event) {
    var prevBtn = event.target.closest('[data-card-media-prev]');
    var nextBtn = event.target.closest('[data-card-media-next]');
    var sizeBtn = event.target.closest('[data-card-size-btn]');
    var sizeTrigger = event.target.closest('[data-card-size-trigger]');
    var sizeBackdrop = event.target.closest('[data-card-size-backdrop]');
    var sizeClose = event.target.closest('[data-card-size-close]');

    if (prevBtn) {
      event.preventDefault();
      event.stopPropagation();
      cycleMedia(prevBtn.closest('product-card'), 'prev');
      return;
    }

    if (nextBtn) {
      event.preventDefault();
      event.stopPropagation();
      cycleMedia(nextBtn.closest('product-card'), 'next');
      return;
    }

    if (sizeTrigger) {
      event.preventDefault();
      event.stopPropagation();
      openSizeSheet(sizeTrigger.closest('product-card'));
      return;
    }

    if (sizeBackdrop) {
      event.preventDefault();
      event.stopPropagation();
      closeSizeSheet(sizeBackdrop.closest('product-card') || sizeBackdrop.__ownerCard);
      return;
    }

    if (sizeClose) {
      event.preventDefault();
      event.stopPropagation();
      var sheetEl = sizeClose.closest('[data-card-size-sheet]');
      closeSizeSheet(sheetEl ? sheetEl.__ownerCard : null);
      return;
    }

    if (sizeBtn) {
      event.preventDefault();
      event.stopPropagation();
      handleSizeClick(sizeBtn);
      return;
    }
  });

  bindAllCards();

  // Re-bind hover preview on any product cards added later (AJAX filtering, pagination, etc.)
  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (node.matches && node.matches('product-card')) {
            bindHoverPreview(node);
          }

          bindAllCards(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();